import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import logoVarTwo from '@/assets/images/svg/logo_var_two.svg';
import clsx from 'clsx';
import { GeometricFrame } from '@/components/common/GeometricFrame';
import { useEchoStore } from '@/context/EchoContext';
import { useUiStore } from '@/store/uiStore';

/**
 * CreateBanner Component
 *
 * Displays a promotional banner in the account section.
 * - If the user is a creator: Shows a "View Your Echo" banner with a solid gradient border.
 * - If the user is not a creator: Shows a "Start Creating" banner with a dashed geometric frame.
 */
export const CreateBanner: React.FC = () => {
  const { myEchoes } = useEchoStore();
  const navigate = useNavigate();
  const openModal = useUiStore((state) => state.openModal);

  // Check if user is a creator
  const isCreator = myEchoes.length > 0;

  const handleAction = () => {
    if (isCreator) {
      navigate('/my-echo');
    } else {
      openModal('creator');
    }
  };

  // --- CREATOR STATE (Solid Gradient Border) ---
  if (isCreator) {
    return (
      <div
        className={clsx(
          'relative mb-8 w-full rounded-20',
          // Gradient Border implementation using padding
          'p-[6px]',
          // Mobile centering wrapper
          'mx-auto md:mx-0 md:w-3/4'
        )}
        style={{
          background: 'linear-gradient(270deg, #ABF56E 0%, #69C71C 100%)',
        }}
      >
        <div
          className={clsx(
            'flex h-auto w-full flex-col gap-6 rounded-[14px] p-6',
            'md:h-create-banner md:flex-row md:items-center md:justify-between md:gap-0 md:px-8 md:py-0',
            'bg-create-banner-gradient' // Use Tailwind utility instead of inline style
          )}
        >
          {/* Content Wrapper */}
          <div className='z-10 flex w-full flex-col text-white md:max-w-[70%] md:justify-center'>
            {/* Title & Logo Row */}
            <div className='mb-2 flex flex-row items-center justify-center gap-2 md:justify-start'>
              <span className='whitespace-nowrap font-sans text-banner-title-mobile font-medium text-white md:text-banner-title'>
                You&apos;re a
              </span>
              <img src={logoVarTwo} alt='d.echo' className='h-[58px] w-[124px] object-contain' />
              <span className='whitespace-nowrap font-sans text-banner-title-mobile font-medium text-white md:text-banner-title'>
                Creator!
              </span>
            </div>

            {/* Description Text */}
            <p className='text-center font-sans text-14 font-normal leading-20 text-white opacity-90 md:text-left md:text-[15px]'>
              Your digital echoes are live and engaging with fans 24/7. You have full access to
              creator tools, analytics, and revenue tracking. Keep creating amazing experiences!
            </p>
          </div>

          {/* Action Button */}
          <div className='z-10 w-full shrink-0 md:w-auto'>
            <Button
              onClick={handleAction}
              className='h-[43px] w-full rounded-30 text-16 font-bold uppercase tracking-normal text-brand-800 md:w-[220px]'
              variant='create-echo'
            >
              VIEW YOUR ECHO
            </Button>
          </div>

          {/* Background Decoration (Glow) */}
          <div className='pointer-events-none absolute right-0 top-0 z-0 h-full w-1/2 rounded-r-[14px] bg-gradient-to-l from-purple-500/20 to-transparent' />
        </div>
      </div>
    );
  }

  // --- DEFAULT STATE (Dashed Geometric Border) ---
  return (
    <div
      className={clsx(
        // Mobile: w-full, auto height, vertical flex
        'flex h-auto w-full flex-col gap-6 p-6',
        // Desktop: 3/4 width, fixed height, horizontal flex
        'md:h-create-banner md:w-3/4 md:flex-row md:gap-0 md:px-8 md:py-0',
        'relative mb-8 items-center justify-between overflow-hidden rounded-20 shadow-lg',
        // Centering logic
        'mx-auto md:mx-0',
        'bg-create-banner-gradient' // Use Tailwind utility
      )}
    >
      <GeometricFrame
        stroke='#BFB0F8'
        strokeWidth={3}
        radius={19}
        hMode='edge'
        hSize={60}
        hGap={30}
        hLine={true}
        hDashArray='100 60'
        vMode='edge'
        vSize={30}
        vGap={0}
        vLine={false}
      />

      <div className='z-10 flex w-full flex-col text-white md:max-w-[65%] md:justify-center'>
        <div className='mb-2 flex flex-row items-center justify-center gap-2 md:justify-start'>
          <span className='mb-2 whitespace-nowrap font-sans text-banner-title-mobile font-medium text-white md:text-banner-title'>
            Create your own
          </span>
          <img
            src={logoVarTwo}
            alt='d.echo'
            className='object-contain'
            style={{ width: '124px', height: '58px' }}
          />
        </div>

        <p className='text-center font-sans text-banner-text-mobile font-normal leading-27 text-white opacity-90 md:whitespace-nowrap md:text-left md:text-16 md:leading-20'>
          Share your legacy with the world. Create your digital echo and inspire millions of fans
          24/7.
        </p>
      </div>

      <div className='z-10 w-full shrink-0 md:w-auto'>
        <Button
          onClick={handleAction}
          className='h-[43px] w-full rounded-30 text-16 font-bold uppercase tracking-normal text-brand-800 md:w-[220px]'
          variant='create-echo'
        >
          START CREATING
        </Button>
      </div>

      <div className='pointer-events-none absolute right-0 top-0 z-0 h-full w-1/2 bg-gradient-to-l from-purple-500/20 to-transparent' />
    </div>
  );
};
